import { useState, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { track } from "../lib/usage";
import Turnstile from "../components/Turnstile";

const USERNAME_RE = /^[A-Za-z0-9_]{3,20}$/;

export default function AuthPage({ mode }) {
  const isSignup = mode === "signup";
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [forgotBusy, setForgotBusy] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaFailed, setCaptchaFailed] = useState(false);
  const [captchaNonce, setCaptchaNonce] = useState(0); // key bump remounts the widget
  const [over18, setOver18] = useState(false);
  const turnstileRef = useRef(null);
  const captchaEnabled = !!import.meta.env.VITE_TURNSTILE_SITE_KEY;
  // Stable identity: an inline arrow here re-rendered the Turnstile widget on
  // every keystroke (its render effect keyed on the callback props) — the
  // flickering, never-completing check. The widget is also remount-proof now,
  // but stable callbacks are the contract its docstring asks for.
  const onCaptchaError = useCallback(() => setCaptchaFailed(true), []);

  // Turnstile tokens are single-use; after each auth attempt clear ours and
  // ask the widget for a fresh one so the next try (or retry) has a valid token.
  function resetCaptcha() {
    setCaptchaToken(null);
    turnstileRef.current?.reset();
  }

  async function handleGoogle() {
    setErr(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/app` },
    });
    if (error) setErr(error.message);
  }

  async function handleForgot() {
    setErr(null);
    if (!email) {
      return setErr("Enter your email above first, then tap \u201CForgot password?\u201D.");
    }
    if (captchaEnabled && !captchaToken) {
      return setErr("Please complete the \u201CI\u2019m human\u201D check first.");
    }
    setForgotBusy(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset`,
        captchaToken,
      });
      if (error) {
        // A failed email send comes back as an empty 500 whose message is
        // literally "{}" — show something actionable instead.
        const msg = (error.message || "").trim();
        return setErr(
          !msg || msg === "{}"
            ? "We couldn't send the reset email right now. Please try again in a moment."
            : msg
        );
      }
      setResetSent(true);
    } catch {
      setErr("We couldn't reach the server. Check your connection and try again.");
    } finally {
      setForgotBusy(false);
      resetCaptcha();
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(null);

    if (isSignup) {
      const uname = username.trim();
      if (!USERNAME_RE.test(uname)) {
        return setErr("Username must be 3–20 characters: letters, numbers, or underscores.");
      }
      setBusy(true);
      track("signup_started");

      // availability check (case-insensitive, LIKE wildcards escaped so a
      // "_" in the handle isn't treated as a wildcard). The DB unique index
      // is the real enforcement; on a rare signup race the handle_new_user
      // trigger falls back to a NULL username and the app routes the user to
      // "Pick your handle" to choose another. This check is friendly UX only.
      const pat = uname.replace(/[\\%_]/g, "\\$&");
      const { data: taken } = await supabase
        .from("profiles")
        .select("username")
        .ilike("username", pat)
        .maybeSingle();
      if (taken) {
        setBusy(false);
        return setErr("That username is taken — try another.");
      }

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: { username: uname, age_confirmed: true },
          emailRedirectTo: `${window.location.origin}/app`,
          captchaToken,
        },
      });
      setBusy(false);
      if (error) {
        resetCaptcha();
        return setErr(error.message);
      }
      track("signup_completed");
      if (data.session) return navigate("/app");
      setSent(true); // email confirmation required
    } else {
      setBusy(true);
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });
      setBusy(false);
      if (error) {
        resetCaptcha();
        return setErr(error.message);
      }
      navigate("/app");
    }
  }

  if (sent) {
    return (
      <main className="site center">
        <div className="auth-card">
          <h2>Check your email</h2>
          <p className="muted">
            We sent a confirmation link to <b>{email}</b>. Click it to activate
            your account, and you'll land right at the tables.
          </p>
          <p className="muted small">
            Nothing arriving? Check spam — delivery can take a few minutes.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="site center">
      <div className="auth-card">
        <h2>{isSignup ? "Create your account" : "Welcome back"}</h2>

        <button type="button" className="sbtn google" onClick={handleGoogle} disabled={busy}>
          Continue with Google
        </button>
        <div className="auth-divider"><span>or</span></div>

        <form onSubmit={handleSubmit}>
          {isSignup && (
            <label className="field">
              Username
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="card_shark_22"
                autoComplete="username"
                maxLength={20}
                required
              />
            </label>
          )}
          <label className="field">
            Email
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label className="field">
            Password
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete={isSignup ? "new-password" : "current-password"}
              minLength={8}
              required
            />
          </label>

          {isSignup && (
            <label className="age-check">
              <input
                type="checkbox"
                checked={over18}
                onChange={(e) => setOver18(e.target.checked)}
              />
              <span>I am 18 years of age or older.</span>
            </label>
          )}

          {err && <div className="auth-error" role="alert">{err}</div>}

          <Turnstile
            key={captchaNonce}
            ref={turnstileRef}
            onVerify={setCaptchaToken}
            onError={onCaptchaError}
          />
          {captchaFailed && !captchaToken && (
            <div className="auth-error" role="alert">
              The &ldquo;I&rsquo;m human&rdquo; check couldn&rsquo;t load — an ad
              blocker or network filter may be blocking Cloudflare. Allow{" "}
              <code>challenges.cloudflare.com</code>, then{" "}
              <button
                type="button"
                className="auth-link-btn"
                onClick={() => {
                  setCaptchaFailed(false);
                  setCaptchaNonce((n) => n + 1);
                }}
              >
                try again
              </button>
              .
            </div>
          )}

          <button
            className="sbtn primary wide"
            type="submit"
            disabled={busy || (captchaEnabled && !captchaToken) || (isSignup && !over18)}
          >
            {busy ? "One moment…" : isSignup ? "Sign up" : "Log in"}
          </button>

          {isSignup && (
            <p className="muted small auth-consent">
              By creating an account, you agree to our{" "}
              <a href="/terms.html" target="_blank" rel="noopener">Terms of Service</a> and{" "}
              <a href="/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>.
            </p>
          )}
        </form>

        <p className="muted small">
          {isSignup ? (
            <>Already have an account? <Link to="/login">Log in</Link></>
          ) : (
            <>New here? <Link to="/signup">Create an account</Link></>
          )}
        </p>

        {!isSignup && (
          <p className="muted small">
            {resetSent ? (
              "Check your email for a reset link."
            ) : (
              <button
                type="button"
                onClick={handleForgot}
                disabled={forgotBusy}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  font: "inherit",
                  color: "var(--brass)",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
              >
                {forgotBusy ? "Sending reset link…" : "Forgot password?"}
              </button>
            )}
          </p>
        )}
      </div>
    </main>
  );
}
